import { useEffect, useRef, useSyncExternalStore } from 'react';

export type UseDocumentVisibilityOptions = {
  onHidden?: () => void;
  onVisible?: () => void;
};

function subscribe(onStoreChange: () => void) {
  document.addEventListener('visibilitychange', onStoreChange);
  return () => document.removeEventListener('visibilitychange', onStoreChange);
}

export function useDocumentVisibility({ onHidden, onVisible }: UseDocumentVisibilityOptions = {}) {
  const visibilityState = useSyncExternalStore<DocumentVisibilityState>(
    subscribe,
    () => document.visibilityState,
    () => 'visible',
  );

  const prevRef = useRef<DocumentVisibilityState | null>(null);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = visibilityState;

    if (prev === null) {
      return;
    }
    if (prev === visibilityState) {
      return;
    }

    if (visibilityState === 'hidden') {
      onHidden?.();
    } else if (visibilityState === 'visible') {
      onVisible?.();
    }
  }, [visibilityState, onHidden, onVisible]);

  return {
    visibilityState,
    isVisible: visibilityState === 'visible',
  };
}
